import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { driversApi } from '../../api/services';
import { driverLabel } from '../../utils/activities';

/* Hook: lista vozača filtrirana po izabranim kompanijama (isti upit kao stari `drivers/all`). */
export function useDriverList(initialCompanies = []) {
    const [companyIds, setCompanyIds] = useState(initialCompanies);
    const [drivers, setDrivers] = useState([]);
    const [driver, setDriver] = useState(null);

    const seq = useRef(0); // brzi uzastopni izbori kompanija: važi samo poslednji odgovor

    /* keep: zadrži izabranog vozača ako je u novoj listi; fallback 'first' = inače prvi, 'none' = inače bez izbora */
    const reload = useCallback(async (ids = companyIds, keep = true, fallback = 'first') => {
        const my = ++seq.current;
        const res = await driversApi.all({ companyIds: ids });
        if (my !== seq.current) return res.drivers;
        const first = fallback === 'first' ? res.drivers[0] || null : null;
        setDrivers(res.drivers);
        setDriver((cur) => (keep && cur ? res.drivers.find((d) => d.id === cur.id) || first : res.drivers[0] || null));
        return res.drivers;
    }, [companyIds]);

    useEffect(() => { reload(companyIds, false).catch(() => {}); }, []); // eslint-disable-line

    /* Izbor kompanije odmah osvežava listu vozača; vozač koji nije u izabranim firmama se poništava. */
    const changeCompanies = (ids) => {
        setCompanyIds(ids);
        reload(ids, true, 'none').catch(() => {});
    };

    return { companyIds, setCompanyIds, changeCompanies, drivers, driver, setDriver, reload };
}

/** Kompanija (multiselect) + osveži + Vozač (dropdown). `children` se prikazuju u istom redu. */
export default function DriverFilters({ list, children, showDriver = true, driverLabelText = 'Vozač', required, onRefreshed }) {
    const companies = useSelector((s) => s.companies.list);
    const { companyIds, changeCompanies, drivers, driver, setDriver, reload } = list;
    const id = useId();
    return (
        <div className="filters">
            <div className="field" style={{ flexBasis: '15rem' }}>
                <label htmlFor={`${id}-c`}>Kompanija</label>
                <div className="row-inline">
                    <MultiSelect inputId={`${id}-c`} value={companyIds} options={companies} optionLabel="name" optionValue="id" placeholder="Izaberite..."
                        onChange={(e) => changeCompanies(e.value)} filter maxSelectedLabels={1} selectedItemsLabel="{0} izabrano" />
                    <Button type="button" icon="pi pi-refresh" text severity="secondary" tooltip="Osveži listu vozača" aria-label="Osveži listu vozača"
                        onClick={async () => {
                            try {
                                const all = await reload(companyIds, true);
                                if (onRefreshed) onRefreshed(all);
                            } catch { /* toast */ }
                        }} />
                </div>
            </div>
            {showDriver && (
                <div className="field grow">
                    <label htmlFor={`${id}-d`}>{driverLabelText}{required && <span className="req" aria-hidden="true">*</span>}</label>
                    <Dropdown inputId={`${id}-d`} value={driver} options={drivers.map((d) => ({ label: driverLabel(d), value: d }))} dataKey="id"
                        onChange={(e) => setDriver(e.value)} filter emptyMessage="Nema pronađenih vozača" emptyFilterMessage="Nema rezultata"
                        placeholder="Izaberite vozača" virtualScrollerOptions={drivers.length > 10 ? { itemSize: 40 } : undefined}
                        className={required && !driver ? 'p-invalid' : ''} aria-required={required || undefined} />
                </div>
            )}
            {children}
        </div>
    );
}
